"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import { anyTermMatches, everyTermMatches } from "@/lib/blacklist-match"

export interface BlacklistRule {
  id: string
  tags: string[]
  users: string[]
  createdAt: number
}

/** A viewer's own named group of tags (e.g. "AI"), which they can rename and switch on or off as a whole. Its tags are visible to them. */
export interface BlacklistGroup {
  id: string
  name: string
  tags: string[]
  enabled: boolean
  createdAt: number
}

interface BlacklistStore {
  blacklistedTags: string[]
  blacklistedUsers: string[]
  rules: BlacklistRule[]
  groups: BlacklistGroup[]
  enableBlacklist: boolean
  /** Set once the old "hated" tags (Tag Filters) have been moved into this blacklist. */
  hatedTagsMoved: boolean
  addTag: (tag: string) => void
  removeTag: (tag: string) => void
  addUser: (user: string) => void
  removeUser: (user: string) => void
  addRule: (rule: { tags?: string[]; users?: string[] }) => void
  removeRule: (ruleId: string) => void
  createGroup: (name: string, tags?: string[]) => string | null
  renameGroup: (id: string, name: string) => void
  deleteGroup: (id: string) => void
  addTagToGroup: (id: string, tag: string) => void
  removeTagFromGroup: (id: string, tag: string) => void
  toggleGroup: (id: string) => void
  isTagBlacklisted: (tag: string) => boolean
  toggleBlacklist: () => void
  isPostBlacklisted: (tags: string[], user?: string) => boolean
}

const cleanTagOf = (tag: string) => tag.trim().toLowerCase()

/**
 * One-time move of the old Tag Filters "hated" tags into the blacklist ("hate" now means "hide", in one place). Reads the Tag Filters
 * store's saved data directly, so the order the two stores load in does not matter. The hated entries are left where they were; Tag
 * Filters no longer acts on them.
 */
export function readOldHatedTags(): string[] {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("tag-filters") : null
    const filters = raw ? JSON.parse(raw)?.state?.tagFilters : null
    return Array.isArray(filters)
      ? filters.filter((f: any) => f?.type === "hate" && typeof f?.tag === "string").map((f: any) => cleanTagOf(f.tag)).filter(Boolean)
      : []
  } catch {
    return []
  }
}

export const useBlacklistStore = create<BlacklistStore>()(
  persist(
    (set, get) => ({
      blacklistedTags: [],
      blacklistedUsers: [],
      rules: [],
      groups: [],
      enableBlacklist: true,
      hatedTagsMoved: false,

      createGroup: (name: string, tags: string[] = []) => {
        const cleanName = name.trim().slice(0, 40)
        if (!cleanName) return null
        const id = `group_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
        const cleanTags = Array.from(new Set(tags.map(cleanTagOf).filter(Boolean)))
        set((state) => ({ groups: [...state.groups, { id, name: cleanName, tags: cleanTags, enabled: true, createdAt: Date.now() }] }))
        return id
      },

      renameGroup: (id: string, name: string) => {
        const cleanName = name.trim().slice(0, 40)
        if (!cleanName) return
        set((state) => ({ groups: state.groups.map((g) => (g.id === id ? { ...g, name: cleanName } : g)) }))
      },

      deleteGroup: (id: string) => {
        set((state) => ({ groups: state.groups.filter((g) => g.id !== id) }))
      },

      addTagToGroup: (id: string, tag: string) => {
        const cleanTag = cleanTagOf(tag)
        if (!cleanTag) return
        set((state) => ({
          groups: state.groups.map((g) => (g.id === id && !g.tags.includes(cleanTag) ? { ...g, tags: [...g.tags, cleanTag] } : g)),
        }))
      },

      removeTagFromGroup: (id: string, tag: string) => {
        const cleanTag = cleanTagOf(tag)
        set((state) => ({ groups: state.groups.map((g) => (g.id === id ? { ...g, tags: g.tags.filter((t) => t !== cleanTag) } : g)) }))
      },

      toggleGroup: (id: string) => {
        set((state) => ({ groups: state.groups.map((g) => (g.id === id ? { ...g, enabled: !g.enabled } : g)) }))
      },

      isTagBlacklisted: (tag: string) => {
        const cleanTag = cleanTagOf(tag)
        const { blacklistedTags, groups } = get()
        return blacklistedTags.includes(cleanTag) || groups.some((g) => g.enabled && g.tags.includes(cleanTag))
      },

      addTag: (tag: string) => {
        const cleanTag = tag.trim().toLowerCase()
        if (!cleanTag) return
        set((state) => ({
          blacklistedTags: state.blacklistedTags.includes(cleanTag)
            ? state.blacklistedTags
            : [...state.blacklistedTags, cleanTag],
        }))
      },

      removeTag: (tag: string) => {
        const cleanTag = tag.trim().toLowerCase()
        set((state) => ({
          blacklistedTags: state.blacklistedTags.filter((t) => t !== cleanTag),
        }))
      },

      addUser: (user: string) => {
        const cleanUser = user.trim().toLowerCase()
        if (!cleanUser) return
        set((state) => ({
          blacklistedUsers: state.blacklistedUsers.includes(cleanUser)
            ? state.blacklistedUsers
            : [...state.blacklistedUsers, cleanUser],
        }))
      },

      removeUser: (user: string) => {
        const cleanUser = user.trim().toLowerCase()
        set((state) => ({
          blacklistedUsers: state.blacklistedUsers.filter((u) => u !== cleanUser),
        }))
      },

      addRule: (rule) => {
        const cleanTags = (rule.tags || [])
          .map((t) => t.trim().toLowerCase())
          .filter((t) => t.length > 0)
        const cleanUsers = (rule.users || [])
          .map((u) => u.trim().toLowerCase())
          .filter((u) => u.length > 0)

        const uniqueTags = Array.from(new Set(cleanTags))
        const uniqueUsers = Array.from(new Set(cleanUsers))

        if (uniqueTags.length === 0 && uniqueUsers.length === 0) return

        set((state) => {
          const exists = state.rules.some((r) => {
            if (r.tags.length !== uniqueTags.length || r.users.length !== uniqueUsers.length) return false
            const tagSet = new Set(r.tags)
            const userSet = new Set(r.users)
            return uniqueTags.every((t) => tagSet.has(t)) && uniqueUsers.every((u) => userSet.has(u))
          })
          if (exists) return state

          const newRule: BlacklistRule = {
            id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            tags: uniqueTags,
            users: uniqueUsers,
            createdAt: Date.now(),
          }

          return { rules: [newRule, ...state.rules] }
        })
      },

      removeRule: (ruleId) => {
        set((state) => ({
          rules: state.rules.filter((r) => r.id !== ruleId),
        }))
      },

      toggleBlacklist: () => {
        set((state) => ({ enableBlacklist: !state.enableBlacklist }))
      },

      isPostBlacklisted: (tags: string[], user?: string) => {
        const { blacklistedTags, blacklistedUsers, rules, groups, enableBlacklist } = get()
        if (!enableBlacklist) return false

        const postTags = (tags || []).map((t) => t.toLowerCase())
        const postUser = user?.trim().toLowerCase()

        // Whole-tag matching (lib/blacklist-match.ts): "ai" no longer hides every tag that merely contains "ai".
        const hasBlacklistedTag = anyTermMatches(blacklistedTags, postTags)

        // The viewer's own named groups that are switched on.
        const hasGroupTag = (groups || []).some((g) => g.enabled && anyTermMatches(g.tags, postTags))

        const hasBlacklistedUser = postUser ? blacklistedUsers.includes(postUser) : false

        const hasRuleMatch = rules.some((rule) => {
          const tagsMatch = rule.tags.length === 0 || everyTermMatches(rule.tags, postTags)
          const usersMatch = rule.users.length === 0 || (postUser ? rule.users.includes(postUser) : false)
          return tagsMatch && usersMatch
        })

        return hasBlacklistedTag || hasGroupTag || hasBlacklistedUser || hasRuleMatch
      },
    }),
    {
      name: "blacklist-settings",
      version: 3,
      migrate: (persistedState: any) => {
        if (!persistedState) {
          return {
            blacklistedTags: [],
            blacklistedUsers: [],
            rules: [],
            groups: [],
            enableBlacklist: true,
            hatedTagsMoved: false,
          }
        }
        return {
          ...persistedState,
          rules: Array.isArray(persistedState.rules) ? persistedState.rules : [],
          groups: Array.isArray(persistedState.groups) ? persistedState.groups : [],
        }
      },
      onRehydrateStorage: () => (state) => {
        if (!state || state.hatedTagsMoved) return
        const hated = readOldHatedTags()
        useBlacklistStore.setState((current) => ({
          blacklistedTags: Array.from(new Set([...current.blacklistedTags, ...hated])),
          hatedTagsMoved: true,
        }))
      },
    },
  ),
)
